import { api } from './api';

export type Lancamento = {
  id: number;
  tipo: 'entrada' | 'saida';
  categoria: string;
  valorCentavos: number;
  dia: string;
  descricao: string;
  ministerioId: number | null;
  ministerioNome: string | null;
  forma: string;
  membroId: number | null;
  membroNome: string | null;
  estornaId: number | null;
  estornadoPor: number | null;
};

export type Resumo = {
  ano: number;
  mes: number;
  fechado: boolean;
  entradasCentavos: number;
  saidasCentavos: number;
  saldoCentavos: number;
  entradasPorCategoria: { categoria: string; centavos: number }[];
  saidasPorCategoria: { categoria: string; centavos: number }[];
  saidasPorMinisterio: { id: number | null; nome: string; centavos: number }[];
  orcamentos: {
    ministerioId: number;
    ministerioNome: string;
    tetoCentavos: number;
    acao: string;
    gastoCentavos: number;
  }[];
};

export type Orcamento = {
  id: number;
  ministerioId: number;
  ministerioNome: string;
  ano: number;
  mes: number;
  tetoCentavos: number;
  acao: string;
};

export type Fechamento = {
  id: number;
  ano: number;
  mes: number;
  observacao: string | null;
  quem: string | null;
};

export const CATEGORIAS_ENTRADA = ['dizimo', 'oferta', 'designada', 'doacao', 'outra'] as const;
export const CATEGORIAS_SAIDA = [
  'salario',
  'missoes',
  'manutencao',
  'equipamento',
  'material',
  'servico',
  'ajuda',
  'outra',
] as const;
export const FORMAS = ['pix', 'dinheiro', 'cartao', 'transferencia', 'boleto'] as const;

export const NOME_CATEGORIA: Record<string, string> = {
  dizimo: 'Dizimo',
  oferta: 'Oferta',
  designada: 'Oferta designada',
  doacao: 'Doacao',
  salario: 'Salario',
  missoes: 'Missoes',
  manutencao: 'Manutencao',
  equipamento: 'Equipamento',
  material: 'Material',
  servico: 'Servico',
  ajuda: 'Ajuda social',
  outra: 'Outra',
};

export const NOME_FORMA: Record<string, string> = {
  pix: 'PIX',
  dinheiro: 'Dinheiro',
  cartao: 'Cartao',
  transferencia: 'Transferencia',
  boleto: 'Boleto',
};

/** "R$ 1.234,56" a partir de centavos. Nunca divide para depois somar. */
export function reais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * "1.234,56", "1234,56" ou "1234.56" viram 123456 centavos.
 * A conversao acontece aqui, no limite da tela: do formulario para o servidor
 * so trafega inteiro, para nunca existir float no caminho do dinheiro.
 */
export function paraCentavos(texto: string): number | null {
  const limpo = texto.trim().replace(/[R$\s]/g, '');
  if (!limpo) return null;
  // com virgula, o ponto e separador de milhar; sem virgula, o ponto e decimal
  const normalizado = limpo.includes(',')
    ? limpo.replace(/\./g, '').replace(',', '.')
    : limpo;
  if (!/^\d+(\.\d{1,2})?$/.test(normalizado)) return null;
  const [inteira, decimal = ''] = normalizado.split('.');
  return Number(inteira) * 100 + Number(decimal.padEnd(2, '0'));
}

export const buscarLancamentos = (ano: number, mes: number, tipo = '') =>
  api<{
    ano: number;
    mes: number;
    lancamentos: Lancamento[];
    fechado: boolean;
    podeEditar: boolean;
  }>(`/financeiro/lancamentos?ano=${ano}&mes=${mes}${tipo ? `&tipo=${tipo}` : ''}`);

export const buscarResumo = (ano: number, mes: number) =>
  api<Resumo>(`/financeiro/resumo?ano=${ano}&mes=${mes}`);

export const criarLancamento = (dados: {
  tipo: 'entrada' | 'saida';
  categoria: string;
  valorCentavos: number;
  dia: string;
  descricao: string;
  ministerioId?: number | null;
  forma?: string;
  confirmarMesmoAssim?: boolean;
}) => api<{ id: number }>('/financeiro/lancamentos', { method: 'POST', body: JSON.stringify(dados) });

export const estornarLancamento = (id: number, motivo: string) =>
  api<{ id: number }>(`/financeiro/lancamentos/${id}/estornar`, {
    method: 'POST',
    body: JSON.stringify({ motivo }),
  });

export const buscarOrcamentos = (ano: number) =>
  api<{ ano: number; orcamentos: Orcamento[]; podeEditar: boolean }>(
    `/financeiro/orcamentos?ano=${ano}`,
  );

export const salvarOrcamento = (dados: {
  ministerioId: number;
  ano: number;
  mes: number;
  tetoCentavos: number;
  acao: string;
}) => api<{ id: number; atualizado: boolean }>('/financeiro/orcamentos', {
  method: 'POST',
  body: JSON.stringify(dados),
});

export const buscarFechamentos = () =>
  api<{ fechamentos: Fechamento[] }>('/financeiro/fechamentos');

export const fecharMes = (ano: number, mes: number, observacao?: string | null) =>
  api<{ id: number }>('/financeiro/fechamentos', {
    method: 'POST',
    body: JSON.stringify({ ano, mes, ...(observacao ? { observacao } : {}) }),
  });

export const reabrirMes = (id: number) =>
  api(`/financeiro/fechamentos/${id}`, { method: 'DELETE' });

/* ------------------ ano, conciliacao, igreja e documentos ------------------ */

export type Anual = {
  ano: number;
  meses: {
    mes: number;
    entradasCentavos: number;
    saidasCentavos: number;
    saldoCentavos: number;
    acumuladoCentavos: number;
    fechado: boolean;
  }[];
  entradasPorCategoria: { categoria: string; centavos: number }[];
  saidasPorCategoria: { categoria: string; centavos: number }[];
  saidasPorMinisterio: { id: number | null; nome: string; centavos: number }[];
  orcamentos: {
    ministerioId: number;
    ministerioNome: string;
    tetoCentavos: number;
    gastoCentavos: number;
  }[];
  totais: { entradasCentavos: number; saidasCentavos: number; saldoCentavos: number };
};

export type SugestaoDeCasamento = {
  id: number;
  dia: string;
  descricao: string;
  tipo: string;
  valorCentavos: number;
  diasDeDiferenca: number;
};

export type LinhaDoPainel = {
  id: number;
  fitid: string;
  dia: string;
  valorCentavos: number;
  descricao: string;
  banco: string;
  conta: string;
  ignorada: boolean;
  lancamentoId: number | null;
  lancamentoDescricao: string | null;
  sugestoes: SugestaoDeCasamento[];
};

export type Painel = {
  ano: number;
  mes: number;
  linhas: LinhaDoPainel[];
  soNoCaixa: {
    id: number;
    dia: string;
    descricao: string;
    tipo: string;
    valorCentavos: number;
    forma: string;
  }[];
  soNoCaixaEmEspecie: number;
  totalConciliadas: number;
  totalPendentes: number;
  podeEditar: boolean;
};

export type Extrato = {
  id: number;
  banco: string;
  conta: string;
  diaInicio: string;
  diaFim: string;
  arquivoNome: string;
  quem: string | null;
  linhas: number;
};

export type DadosIgreja = {
  nome: string;
  cnpj: string;
  endereco: string;
  cidade: string;
  responsavel: string;
};

export const buscarAnual = (ano: number) => api<Anual>(`/financeiro/anual?ano=${ano}`);

export const buscarConciliacao = (ano: number, mes: number) =>
  api<Painel>(`/financeiro/conciliacao?ano=${ano}&mes=${mes}`);

export const buscarExtratos = () => api<{ extratos: Extrato[] }>('/financeiro/extratos');

export const importarExtrato = (nomeArquivo: string, conteudo: string) =>
  api<{ extratoId: number; importadas: number; repetidas: number; casadas: number }>(
    '/financeiro/extratos',
    { method: 'POST', body: JSON.stringify({ nomeArquivo, conteudo }) },
  );

export const casarLinha = (linhaId: number, lancamentoId: number) =>
  api(`/financeiro/conciliacao/${linhaId}/casar`, {
    method: 'POST',
    body: JSON.stringify({ lancamentoId }),
  });

export const descasarLinha = (linhaId: number) =>
  api(`/financeiro/conciliacao/${linhaId}/casar`, { method: 'DELETE' });

export const ignorarLinha = (linhaId: number, ignorada: boolean) =>
  api(`/financeiro/conciliacao/${linhaId}/ignorar`, {
    method: 'POST',
    body: JSON.stringify({ ignorada }),
  });

export const buscarIgreja = () =>
  api<{ igreja: DadosIgreja; podeEditar: boolean }>('/financeiro/igreja');

export const salvarIgreja = (dados: DadosIgreja) =>
  api<{ igreja: DadosIgreja }>('/financeiro/igreja', {
    method: 'PUT',
    body: JSON.stringify(dados),
  });

/**
 * Documento e arquivo, nao JSON: o navegador baixa direto pela rota, que ja e
 * autenticada por cookie. Nada de base64 nem blob guardado em memoria.
 */
export const urlRecibo = (lancamentoId: number) =>
  `/api/financeiro/lancamentos/${lancamentoId}/recibo.pdf`;
export const urlInformeAnual = (membroId: number, ano: number) =>
  `/api/financeiro/informe-anual.pdf?membroId=${membroId}&ano=${ano}`;
export const urlPrestacaoContas = (ano: number, mes?: number) =>
  `/api/financeiro/prestacao-contas.pdf?ano=${ano}${mes ? `&mes=${mes}` : ''}`;
export const urlMovimentoCsv = (ano: number, mes?: number) =>
  `/api/financeiro/movimento.csv?ano=${ano}${mes ? `&mes=${mes}` : ''}`;

/** Parceiro de Deus por equipe: o que cada 12 lançou no mês e os totais. */
export type ParceiroDeDeus = {
  mes: string;
  semanas: string[];
  equipes: { id: number; nome: string; porSemana: Record<string, number>; total: number }[];
  totalGeral: number;
};

export const buscarParceiroDeDeus = (ano: number, mes: number) =>
  api<ParceiroDeDeus>(`/financeiro/parceiro-de-deus?ano=${ano}&mes=${mes}`);
